import React, { useState } from 'react';
import { Sparkles, ArrowUpRight, Copy, Check, ChevronDown, ChevronUp, Bot, RefreshCw } from 'lucide-react';
import { LanguageCode } from '../types';

interface AiTextAssistantProps {
  onTransfer: (generatedText: string) => void;
  currentLang?: LanguageCode;
  fieldLabel?: string;
}

export const AiTextAssistant: React.FC<AiTextAssistantProps> = ({
  onTransfer,
  currentLang = 'TR',
  fieldLabel = 'Açıklama',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [promptInput, setPromptInput] = useState('');
  const [selectedLang, setSelectedLang] = useState<string>(currentLang);
  const [tone, setTone] = useState<'kurumsal' | 'heyecanli' | 'samimi'>('kurumsal');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState('');
  const [copied, setCopied] = useState(false);
  const [transferred, setTransferred] = useState(false);

  const handleGenerate = async () => {
    if (!promptInput.trim()) return;

    setIsGenerating(true);
    setGeneratedText('');
    setTransferred(false);

    try {
      const response = await fetch('/api/ai/generate-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: promptInput,
          lang: selectedLang,
          tone: tone,
          fieldLabel: fieldLabel,
        }),
      });

      const data = await response.json();
      if (data.success && data.text) {
        setGeneratedText(data.text);
      } else {
        // Fallback local intelligent generator
        generateLocalTextFallback();
      }
    } catch {
      generateLocalTextFallback();
    } finally {
      setIsGenerating(false);
    }
  };

  const generateLocalTextFallback = () => {
    const lang = selectedLang.toUpperCase();
    const topic = promptInput.trim();

    let text = '';
    if (lang === 'KM') {
      text = `Pêşniyara Girîng û Agahdarî: ${topic}\n\nKomela me ya çandî bi serbilindî vê xebata taybet radihêjîne pêşberî hemû hemşehriyan. Di çarçoveya parastina nirxên çandî, xebatên ziman û piştevaniya civakî de, em hemû dost û endamên xwe vexwendî vê bernameyê dikin.\n\nJi bo agahdariya zêdetir û beşdarbûnê ji kerema xwe bi rêveberiya komela me re di peywendiyê de bin. Ziman û çanda me nîşana hebûna me ye!`;
    } else if (lang === 'FR') {
      text = `Information Officielle & Communication : ${topic}\n\nNotre association culturelle Franco-Kurde a le plaisir de vous annoncer cette initiative importante. Dans le cadre de la préservation de notre patrimoine culturel, de la solidarité communautaire et du vivre-ensemble, nous invitons chaleureusement l'ensemble de nos membres et amis à participer.\n\nPour toute information complémentaire ou inscription, n'hésitez pas à contacter le secrétariat de notre association.`;
    } else if (lang === 'EN') {
      text = `Official Announcement & Overview: ${topic}\n\nOur French-Kurdish Cultural Association is proud to present this featured event and project. Aimed at preserving our rich cultural heritage, fostering community solidarity, and supporting educational initiatives, we invite all members and guests to join us.\n\nFor further details, registrations, or inquiries, please feel free to reach out to our administration team.`;
    } else {
      text = `Kurumsal Duyuru ve Detaylı Açıklama: ${topic}\n\nYvelines Fransız-Kürt Kültür Derneğimiz, kültürel mirasımızı koruma, toplumsal dayanışmayı güçlendirme ve gelecek nesillere aktarma misyonu doğrultusunda bu önemli çalışmayı hayata geçirmektedir.\n\nTüm hemşehrilerimizi, üyelerimizi ve kültür dostlarımızı bu anlamlı organizasyonumuza katılmaya davet ediyoruz. Detaylı bilgi, kayıt ve iletişim için dernek yönetimimizle iletişime geçebilirsiniz.`;
    }

    setGeneratedText(text);
  };

  const handleTransferClick = () => {
    if (!generatedText) return;
    onTransfer(generatedText);
    setTransferred(true);
    setTimeout(() => {
      setTransferred(false);
      setIsOpen(false);
    }, 1500);
  };

  const handleCopy = () => {
    if (!generatedText) return;
    navigator.clipboard?.writeText(generatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-1.5 mb-2 font-sans">
      {/* Trigger Button / Link under input field */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#38BDF8] hover:text-[#7DD3FC] transition-colors py-1 px-2.5 rounded-lg bg-[#38BDF8]/10 border border-[#38BDF8]/30 hover:border-[#38BDF8] cursor-pointer"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#38BDF8] animate-pulse" />
        <span>✨ AI ile Açıklama Metni Taslağı Oluştur</span>
        {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {/* Expandable AI Drawer / Drawer Container */}
      {isOpen && (
        <div className="mt-2.5 p-4 rounded-2xl bg-[#0B1426] border-2 border-[#38BDF8]/50 shadow-[0_4px_25px_rgba(56,189,248,0.25)] space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between border-b border-[#38BDF8]/20 pb-2">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-[#38BDF8]" />
              <span className="text-xs font-extrabold text-white uppercase tracking-wider">
                Yapay Zeka Metin Yazarı ({fieldLabel})
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-gray-400">Hedef Dil:</span>
              <select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value as LanguageCode)}
                className="bg-[#132238] text-[#38BDF8] border border-[#38BDF8]/40 rounded-lg px-2 py-0.5 font-bold outline-none"
              >
                <option value="TR">Türkçe</option>
                <option value="KM">Kurmancî</option>
                <option value="FR">Français</option>
                <option value="EN">English</option>
              </select>
            </div>
          </div>

          {/* Prompt input field */}
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">
              Yazmak istediğiniz konuyu kısaca özetleyin:
            </label>
            <input
              type="text"
              value={promptInput}
              onChange={(e) => setPromptInput(e.target.value)}
              placeholder="Örn: 21 Mart Newroz şenliği saat 18:00'de yapılacak, halk oyunları ve konser var."
              className="w-full px-3 py-2 rounded-xl bg-[#132238] border border-[#38BDF8]/30 text-white text-xs placeholder-gray-500 focus:border-[#38BDF8] outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleGenerate();
                }
              }}
            />
          </div>

          {/* Tone Selector & Generate Button */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-1 text-[11px]">
              <span className="text-gray-400">Üslup:</span>
              <button
                type="button"
                onClick={() => setTone('kurumsal')}
                className={`px-2 py-0.5 rounded-md font-semibold transition-all ${
                  tone === 'kurumsal' ? 'bg-[#38BDF8] text-[#0B1426]' : 'bg-[#132238] text-gray-300'
                }`}
              >
                Kurumsal
              </button>
              <button
                type="button"
                onClick={() => setTone('heyecanli')}
                className={`px-2 py-0.5 rounded-md font-semibold transition-all ${
                  tone === 'heyecanli' ? 'bg-[#38BDF8] text-[#0B1426]' : 'bg-[#132238] text-gray-300'
                }`}
              >
                Coşkulu/Duyuru
              </button>
              <button
                type="button"
                onClick={() => setTone('samimi')}
                className={`px-2 py-0.5 rounded-md font-semibold transition-all ${
                  tone === 'samimi' ? 'bg-[#38BDF8] text-[#0B1426]' : 'bg-[#132238] text-gray-300'
                }`}
              >
                Kültürel
              </button>
            </div>

            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating || !promptInput.trim()}
              className="px-4 py-1.5 rounded-xl bg-gradient-to-r from-[#38BDF8] to-[#0284C7] hover:from-[#7DD3FC] hover:to-[#38BDF8] text-[#0B1426] text-xs font-black flex items-center gap-1.5 disabled:opacity-50 transition-all cursor-pointer shadow-md"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Hazırlanıyor...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>🤖 Metni Üret</span>
                </>
              )}
            </button>
          </div>

          {/* Generated Result Preview Box */}
          {generatedText && (
            <div className="mt-3 p-3 rounded-xl bg-[#132238] border border-[#38BDF8]/40 space-y-2">
              <div className="flex items-center justify-between text-[11px] text-[#38BDF8] font-bold">
                <span>Üretilen Profesyonel Metin:</span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="text-gray-300 hover:text-white flex items-center gap-1"
                >
                  {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  <span>{copied ? 'Kopyalandı' : 'Kopyala'}</span>
                </button>
              </div>

              <div className="text-xs text-gray-200 whitespace-pre-line leading-relaxed max-h-48 overflow-y-auto pr-1">
                {generatedText}
              </div>

              {/* Bottom Transfer Button */}
              <div className="pt-2 border-t border-[#38BDF8]/20 flex items-center justify-end">
                <button
                  type="button"
                  onClick={handleTransferClick}
                  className={`px-4 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all cursor-pointer ${
                    transferred
                      ? 'bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.6)]'
                      : 'bg-[#38BDF8] hover:bg-[#7DD3FC] text-[#0B1426] shadow-[0_0_15px_rgba(56,189,248,0.5)]'
                  }`}
                >
                  {transferred ? (
                    <>
                      <Check className="w-4 h-4 stroke-[3]" />
                      <span>Üstteki Alana Aktarıldı!</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpRight className="w-4 h-4 stroke-[3]" />
                      <span>📋 Metne Aktar (Yukarıya Yapıştır)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
